import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type LanguagesView, type LaunchVersionView, type ProjectDetail } from "../api.js";
import { AppShell } from "../components/AppShell.js";
import { ProjectNav } from "../components/ProjectNav.js";
import { ErrorNote, Spinner } from "../components/ui.js";
import { anyInProgress } from "../languages/languages.js";
import { ExportPanel } from "../render/ExportPanel.js";
import { ExportPlayer } from "../render/ExportPlayer.js";
import { playerChoices } from "../render/languageExports.js";
import { tabFromQuery, videoLength, type ExportTab } from "../render/exportView.js";
import { ShareSection } from "../share/ShareSection.js";
import { shareRefreshKey } from "../share/shareView.js";
import { launchRows, launchVersionLabel, readyVersions } from "./launchExportView.js";
import { useVersionLanguages } from "./useVersionLanguages.js";

/**
 * A launch film's Export and share: the same page a product video has — the
 * preview beside Download and Share — with a version picker instead of a
 * size, and nothing to render: the film and its languages are made on our
 * servers, so every row is a download.
 */
export const LaunchExport: React.FC<{ project: ProjectDetail }> = ({ project }) => {
  const [search] = useSearchParams();
  const [versions, setVersions] = useState<LaunchVersionView[] | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  /** Each ready version's languages, for the picker's labels and the chosen one's rows. */
  const [views, setViews] = useState<Record<string, LanguagesView>>({});
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ExportTab>(() => tabFromQuery(search.get("tab")));
  const [shareLive, setShareLive] = useState<boolean | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .launchVersions(project.id)
      .then((view) => {
        if (!current) return;
        setVersions(view.versions);
        const ready = readyVersions(view.versions);
        setVersionId((chosen) => chosen ?? ready[0]?.id ?? null);
        for (const version of ready) {
          void api
            .languages(project.id, version.id)
            .then((languages) => {
              if (current) setViews((all) => ({ ...all, [version.id]: languages }));
            })
            .catch(() => undefined);
        }
      })
      .catch((cause: unknown) => {
        if (current) setError(cause instanceof Error ? cause.message : "Could not load the versions");
      });
    return () => {
      current = false;
    };
  }, [project.id]);

  const ready = useMemo(() => readyVersions(versions ?? []), [versions]);
  const version = ready.find((one) => one.id === versionId) ?? null;
  // The chosen version's languages, read again every few seconds while one is
  // being voiced, so "Voicing…" turns to Ready without a reload.
  const chosen = useVersionLanguages(project.id, version?.id ?? null, false);
  const view = version ? (chosen.view ?? views[version.id] ?? null) : null;
  const english =
    version?.videoUrl && version.downloadUrl
      ? { url: version.videoUrl, downloadUrl: version.downloadUrl, filename: null, sizeBytes: null }
      : null;

  const zip = async () => {
    if (!version) return;
    setZipping(true);
    setZipError(null);
    try {
      const { url } = await api.languagesDownloadLink(project.id, version.id);
      window.location.assign(url);
    } catch {
      setZipError("The download couldn't be prepared. Try again in a moment.");
    } finally {
      setZipping(false);
    }
  };

  const rows = launchRows(version && english ? english.downloadUrl : null, view, project.name);
  const CHIP = {
    ok: "bg-[#E7F6EC] text-[#1C6B34]",
    warn: "bg-[#FFF1E0] text-[#8A4300]",
    bad: "bg-[#FDECEC] text-[#B42318]",
  };
  const zippable = (view?.languages ?? []).some((one) => Object.values(one.downloads).some(Boolean));
  const making = anyInProgress(view);

  return (
    <AppShell
      breadcrumb={
        <>
          <Link to="/dashboard" className="shrink-0 text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <Link
            to={`/projects/${project.id}`}
            className="min-w-0 truncate text-ink-400 transition-colors hover:text-ink-200"
          >
            {project.name}
          </Link>
        </>
      }
      nav={<ProjectNav projectId={project.id} kind="launch" current="export" published={shareLive} />}
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1">
            <h1 className="text-[28px] font-semibold tracking-[-0.01em] text-ink-100">Export and share</h1>
            <p className="text-[14px] text-ink-300">
              {version
                ? [
                    version.durationSeconds ? videoLength(version.durationSeconds) : null,
                    "1920×1080",
                    `${ready.length} ${ready.length === 1 ? "version" : "versions"}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : " "}
            </p>
          </div>
          <Link to={`/projects/${project.id}`} className="btn btn-secondary shrink-0">
            Back to studio
          </Link>
        </div>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {versions === null && !error ? (
          <div className="flex justify-center p-10" role="status" aria-label="Loading">
            <Spinner />
          </div>
        ) : null}

        {versions !== null && !version ? (
          <p className="panel p-5 text-[14px] text-ink-300">
            Nothing to export yet: the first version is still being made.
          </p>
        ) : null}

        {version ? (
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="export-version" className="text-[13px] text-ink-300">
                  Version
                </label>
                <select
                  id="export-version"
                  className="input !w-auto cursor-pointer"
                  value={version.id}
                  onChange={(event) => {
                    setVersionId(event.target.value);
                    setOpen(null);
                  }}
                >
                  {ready.map((one, index) => (
                    <option key={one.id} value={one.id}>
                      {launchVersionLabel(one, one.id === version.id ? view : (views[one.id] ?? null), index === 0)}
                    </option>
                  ))}
                </select>
              </div>
              <ExportPlayer key={version.id} choices={playerChoices(english, view, project.name)} />
            </div>

            <ExportPanel
              tab={tab}
              onTab={setTab}
              live={shareLive}
              panels={{
                download: (
                  <div className="flex flex-col gap-5">
                    <p className="rounded-[12px] bg-bay-900 px-3.5 py-3 text-[13px] leading-relaxed text-ink-200">
                      Launch films and their languages are made for you on our servers — nothing to render here. Each
                      file below is ready to download.
                    </p>
                    <section aria-labelledby="launch-videos" className="flex flex-col gap-2">
                      <div className="flex items-baseline gap-2">
                        <h2 id="launch-videos" className="flex-1 text-[13px] font-semibold text-ink-200">
                          {`v${version.number} videos`}
                        </h2>
                        <Link
                          to={`/projects/${project.id}?languages=open&version=${encodeURIComponent(version.id)}`}
                          className="text-[13px] font-semibold text-ember-600 hover:underline"
                        >
                          + Add a language
                        </Link>
                      </div>
                      <div className="overflow-hidden rounded-[12px] border border-[var(--edge)]">
                        {rows.map((row) => {
                          const expanded = open === row.key;
                          const filesId = `launch-row-${row.key}-files`;
                          return (
                            <div key={row.key} className="border-b border-[var(--edge)] last:border-b-0">
                              <div className="flex items-center gap-3 px-3.5 py-3">
                                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                  <span className="truncate text-[15px] font-semibold text-ink-100" lang={row.code}>
                                    {row.name}
                                  </span>
                                  <span className="text-[12px] text-ink-300">{row.depth}</span>
                                </span>
                                <span
                                  className={`shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-[12px] font-semibold ${CHIP[row.chip.tone]}`}
                                >
                                  {row.chip.label}
                                </span>
                                {row.files.length > 0 ? (
                                  <button
                                    type="button"
                                    className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--edge)] bg-white hover:border-ember-500 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)]"
                                    aria-expanded={expanded}
                                    aria-controls={filesId}
                                    aria-label={`Downloads for ${row.name}`}
                                    onClick={() => setOpen(expanded ? null : row.key)}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                                      <path
                                        d="M8 2.5v7.5M4.8 7l3.2 3.2L11.2 7M3 13h10"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                ) : (
                                  <span className="w-[34px] shrink-0" />
                                )}
                              </div>
                              {expanded ? (
                                <div id={filesId} className="flex flex-wrap gap-1.5 px-3.5 pb-3">
                                  {row.files.map((file) => (
                                    <a
                                      key={file.label}
                                      href={file.href}
                                      download={file.download ?? true}
                                      className="rounded-full border border-[var(--edge)] px-2.5 py-1 text-[12.5px] text-ink-100 hover:border-ember-500 hover:text-ember-600"
                                    >
                                      {file.label}
                                    </a>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                      {making ? (
                        <p className="text-[12px] text-ink-300">
                          A language being voiced appears here, and in the shared player, when it is ready.
                        </p>
                      ) : null}
                    </section>
                    {zippable ? (
                      <button type="button" className="btn btn-secondary" disabled={zipping} onClick={() => void zip()}>
                        {zipping ? "Preparing…" : "Download everything · .zip"}
                      </button>
                    ) : null}
                    {zipError ? <p className="text-center text-[12px] text-signal-bad">{zipError}</p> : null}
                  </div>
                ),
                share: (
                  <ShareSection
                    projectId={project.id}
                    projectName={project.name}
                    kind="launch"
                    onLiveChange={setShareLive}
                    refreshKey={shareRefreshKey(ready[0]?.id ?? null, ready[0] ? (views[ready[0].id] ?? null) : null)}
                  />
                ),
              }}
            />
          </div>
        ) : null}
      </div>
    </AppShell>
  );
};
